import { X, Check } from "lucide-react";

const TechnologyAdvantage = () => {
  return (
    <section className="py-24 px-8">
      <div className="max-w-[1100px] mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-light text-foreground mb-4">
            Purpose-Built for Post-House v. NCAA
          </h2>
          <p className="text-lg font-light text-muted-foreground max-w-[700px] mx-auto">
            Why existing solutions fall short for institutional compliance defense
          </p>
        </div>

        <div className="grid gap-6">
          {/* Marketplace Platforms */}
          <div className="border border-border/50 rounded-lg p-6 hover:border-border transition-all">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center flex-shrink-0">
                <X className="w-5 h-5 text-destructive" />
              </div>
              <div>
                <h4 className="text-lg font-semibold mb-2">Marketplace Platforms (INFLCR, Opendorse)</h4>
                <p className="text-muted-foreground font-light mb-3">
                  Built for athlete branding and deal volume, not institutional risk mitigation
                </p>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <X className="w-4 h-4 text-destructive flex-shrink-0 mt-0.5" />
                    <span>Conflict of interest: profit from both deals and compliance</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <X className="w-4 h-4 text-destructive flex-shrink-0 mt-0.5" />
                    <span>Basic audit logs without cryptographic integrity verification</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <X className="w-4 h-4 text-destructive flex-shrink-0 mt-0.5" />
                    <span>No automated Title IX distribution monitoring</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Consulting Firms */}
          <div className="border border-border/50 rounded-lg p-6 hover:border-border transition-all">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center flex-shrink-0">
                <X className="w-5 h-5 text-destructive" />
              </div>
              <div>
                <h4 className="text-lg font-semibold mb-2">Consulting Firms (Traditional Advisory)</h4>
                <p className="text-muted-foreground font-light mb-3">
                  Expert guidance without technology automation or scalability
                </p>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <X className="w-4 h-4 text-destructive flex-shrink-0 mt-0.5" />
                    <span>Manual processes that don't scale with deal volume</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <X className="w-4 h-4 text-destructive flex-shrink-0 mt-0.5" />
                    <span>No real-time conflict detection or automated workflows</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <X className="w-4 h-4 text-destructive flex-shrink-0 mt-0.5" />
                    <span>Expensive hourly billing without technology foundation</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Homegrown Systems */}
          <div className="border border-border/50 rounded-lg p-6 hover:border-border transition-all">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center flex-shrink-0">
                <X className="w-5 h-5 text-destructive" />
              </div>
              <div>
                <h4 className="text-lg font-semibold mb-2">Homegrown Spreadsheets</h4>
                <p className="text-muted-foreground font-light mb-3">
                  Fragile, error-prone systems with no audit defense capability
                </p>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <X className="w-4 h-4 text-destructive flex-shrink-0 mt-0.5" />
                    <span>No immutable audit trails for NCAA investigations</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <X className="w-4 h-4 text-destructive flex-shrink-0 mt-0.5" />
                    <span>Manual error risk increases with program scale</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <X className="w-4 h-4 text-destructive flex-shrink-0 mt-0.5" />
                    <span>Impossible to demonstrate due diligence under scrutiny</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* AthleteNexus Solution */}
          <div className="border-2 border-primary/30 rounded-lg p-6 bg-primary/5">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                <Check className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h4 className="text-lg font-semibold mb-2">AthleteNexus: Pure Institutional Defense</h4>
                <p className="text-muted-foreground font-light mb-3">
                  The only platform designed exclusively for compliance officers and athletic departments
                </p>
                <ul className="space-y-2 text-sm text-foreground/80">
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                    <span className="font-medium">SOC 2 Type II compliant audit trails with cryptographic hash-chain verification</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                    <span className="font-medium">Automated Title IX distribution monitoring (CAPS-compliant)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                    <span className="font-medium">Real-time conflict detection against institutional sponsors</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                    <span className="font-medium">SOC 2 Type II compliant infrastructure</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                    <span className="font-medium">No deal-making conflict: we protect programs, not sell deals</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="text-center mt-12">
          <p className="text-lg font-light text-muted-foreground italic">
            "We're not trying to make deals. We're trying to protect your program."
          </p>
        </div>
      </div>
    </section>
  );
};

export default TechnologyAdvantage;
